import cx from 'classnames'

import { Pressable } from '@react-aria/interactions'
import Styles from './CustomScheduler.module.scss'

import { Text } from 'blaze-ui/Text/Text'
import {
  DayAndTime,
  firstAvailableTime,
  isToday,
  TimeOfDay,
  timeOfDayToDisplay,
  unavailableTimes,
} from '../../StartDateAndPostFrequencyModal'
import dayjs, { Dayjs } from 'dayjs'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Menu } from 'components/Menu/Menu'
import { Item } from '@react-stately/collections'
import { ChevronDown, Close } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import { IconButton } from 'blaze-ui/IconButton/IconButton'

interface Props {
  title: string
  days: DayAndTime[]
  setDays: (days: DayAndTime[]) => void
  startDate: Dayjs
  customTime: string | undefined
}

export const CustomScheduler = ({ title, days, setDays, startDate, customTime }: Props) => {
  return (
    <div className={Styles.container}>
      <div className={Styles.daySelectContainer}>
        <Text variant="placeholder" color="var(--dark-80)">
          {title}
        </Text>
        <div className={Styles.dayOptionsContainer}>
          {[0, 1, 2, 3, 4, 5, 6].map((num) => (
            <Pressable
              key={num}
              onPress={() => {
                if (days.map((d) => d.day).includes(num)) {
                  setDays(days.filter((d) => d.day !== num).sort())
                } else {
                  let newDay = { day: num, time: { ofDay: TimeOfDay.Morning } }

                  if (isToday(startDate) && startDate.day() === num) {
                    const currentHour = dayjs().hour()

                    newDay.time.ofDay = firstAvailableTime(currentHour)
                  }

                  const newDays = [...days, newDay].sort((a, b) => a.day - b.day)

                  setDays(newDays as DayAndTime[])
                }
              }}
            >
              <span className={cx(Styles.dayOption, { [Styles.selected]: days.map((d) => d.day).includes(num) })}>
                {dayjs().day(num).format('ddd')}
              </span>
            </Pressable>
          ))}
        </div>
      </div>
      <div className={Styles.chooseTimesContainer}>
        <Text variant="placeholder" color="var(--dark-80)">
          Choose your times
        </Text>
        <div className={Styles.timesContainer}>
          {days.map((day) => (
            <div className={Styles.timeContainer} key={day.day}>
              <Text variant="smallList">Every {dayjs().day(day.day).format('dddd')}</Text>
              {day.time.ofDay === TimeOfDay.Custom && (
                <div className={Styles.customControls}>
                  <MenuTrigger>
                    <Button variant="tertiary" size="xs" endIcon={ChevronDown} color="var(--dark-80)">
                      {day.time?.at?.hour && typeof day.time.at.minute === 'number'
                        ? `${day.time.at.hour}:${day.time.at.minute === 0 ? '00' : day.time.at.minute}`
                        : 'Pick time'}
                    </Button>
                    <Menu
                      selectionMode="single"
                      selectedKeys={[customTime as string]}
                      onAction={(key) => {
                        const [hour, minute] = (key as string).split(':')
                        const newDays = days.map((oldDay) => {
                          if (oldDay.day === day.day) {
                            return {
                              ...day,
                              time: {
                                ...oldDay.time,
                                at: {
                                  hour: parseInt(hour),
                                  minute: parseInt(minute),
                                  amOrPm: oldDay.time.at?.amOrPm,
                                },
                              },
                            }
                          }

                          return oldDay
                        })
                        setDays(newDays as DayAndTime[])
                      }}
                    >
                      {[...Array(12)].map((_num, index) =>
                        [0, 15, 30, 45].map((minute) => {
                          if (isToday(startDate) && day.day === startDate.day()) {
                            let hour = index

                            if (day.time.at?.amOrPm === 'pm') {
                              hour = index + 12
                            }

                            if (dayjs().hour() > hour) return null
                            if (dayjs().hour() === hour && dayjs().minute() > minute) return null
                          }

                          const time = `${index === 0 ? 12 : index}:${minute === 0 ? '00' : minute}`

                          return (
                            <Item key={time} textValue={time}>
                              {time}
                            </Item>
                          )
                        }),
                      )}
                    </Menu>
                  </MenuTrigger>
                  <MenuTrigger>
                    <Button variant="tertiary" size="xs" endIcon={ChevronDown} color="var(--dark-80)">
                      {day.time.at?.amOrPm.toUpperCase()}
                    </Button>
                    <Menu
                      selectionMode="single"
                      disabledKeys={
                        isToday(startDate) &&
                        day.day === startDate.day() &&
                        dayjs().isAfter(dayjs().hour(11).minute(45))
                          ? ['am']
                          : []
                      }
                      selectedKeys={[day.time.at?.amOrPm as string]}
                      onAction={(key) => {
                        const newDays = days.map((oldDay) => {
                          if (day.day === oldDay.day) {
                            return {
                              ...oldDay,
                              time: { ...oldDay.time, at: { ...oldDay.time.at, amOrPm: key } },
                            }
                          }

                          return oldDay
                        })
                        setDays(newDays as DayAndTime[])
                      }}
                    >
                      <Item key="am">AM</Item>
                      <Item key="pm">PM</Item>
                    </Menu>
                  </MenuTrigger>
                  <IconButton
                    size="xs"
                    variant="tertiary"
                    icon={Close}
                    onPress={() => {
                      const newDays = days.map((oldDay) => {
                        if (day.day === oldDay.day) {
                          let ofDay = TimeOfDay.Morning

                          if (isToday(startDate) && startDate.day() === day.day) {
                            const currentHour = dayjs().hour()

                            ofDay = firstAvailableTime(currentHour)
                          }

                          return { ...oldDay, time: { ofDay } }
                        }

                        return oldDay
                      })

                      setDays(newDays as DayAndTime[])
                    }}
                  />
                </div>
              )}
              {day.time.ofDay !== TimeOfDay.Custom && (
                <MenuTrigger>
                  <Button variant="tertiary" size="xs" endIcon={ChevronDown}>
                    {timeOfDayToDisplay(day.time.ofDay).text}
                  </Button>
                  <Menu
                    selectedKeys={[day.time.ofDay]}
                    disabledKeys={unavailableTimes(day.day, startDate)}
                    selectionMode="single"
                    onAction={(key) => {
                      const newDays = days.map((oldDay) => {
                        if (oldDay.day === day.day) {
                          const newDay = { ...day, time: { ofDay: key, at: {} } }

                          if (key === TimeOfDay.Custom) {
                            let amOrPm = 'am'
                            if (isToday(startDate) && startDate.day() === day.day) {
                              const currentHour = dayjs().hour()

                              amOrPm = firstAvailableTime(currentHour) === TimeOfDay.Morning ? 'am' : 'pm'
                            }
                            newDay.time.at = { hour: undefined, minute: undefined, amOrPm }
                          }

                          return newDay
                        }

                        return oldDay
                      })
                      setDays(newDays as DayAndTime[])
                    }}
                  >
                    {Object.entries(TimeOfDay).map(([_key, value]) => {
                      if (value === TimeOfDay.Custom) {
                        return (
                          <Item key={value} textValue="Let me choose specifically">
                            Let me choose specifically
                          </Item>
                        )
                      }

                      if (value === TimeOfDay.Random) {
                        return (
                          <Item key={value} textValue="Choose randomly for me">
                            Choose randomly for me
                          </Item>
                        )
                      }

                      const textValue = timeOfDayToDisplay(value).text
                      return (
                        <Item key={value} textValue={textValue}>
                          {textValue}
                        </Item>
                      )
                    })}
                  </Menu>
                </MenuTrigger>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
